/**
 * Single browser gate for Grail `/api` calls (Artifice session).
 *
 * Policy, applied here and nowhere else:
 * - httpOnly cookies (`credentials: include`)
 * - in-tab access JWT on `X-Artifice-Access` / `X-Jester-Access`
 * - never `Authorization: Bearer` on Grail (Azure Easy Auth intercepts that header)
 * - Valet account routes keep caller `Authorization` (device-login upgrade)
 * - CSRF `X-Requested-With` if the caller omitted it
 *
 * Hyperliquid public `/info`, Observatory admin, and other origins must keep
 * using raw `fetch`. Same-origin Grail APIs should go through this module
 * (`artificeFetch` / `miniappFetch` / `oroborosFetch`).
 */

import brand from '../config/brand.config';
import { getFrontEndTenantCredentials } from '../miniapp/api/frontEndTenantSession';
import { withSessionAccessHeaders } from '../utils/sessionAccessToken';

export type ArtificeAuthMode = 'session' | 'none';

export type ArtificeFetchInit = Omit<RequestInit, 'credentials'> & {
  /** Attach the live access JWT. Default `session`. Use `none` for public config. */
  auth?: ArtificeAuthMode;
  /** Override `brand.apiBaseUrl` (Oroboros split frontend). */
  apiBaseUrl?: string;
  /** Abort the request after this many ms. 0 / omitted = no client timeout. */
  timeoutMs?: number;
};

/**
 * `window.fetch`-shaped wrapper so vendor login code can pass the gate as `fetch`.
 * Drops `credentials` (always cookies) and only accepts a URL string or Request/URL.
 */
export function artificeFetchCompat(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : String((input as Request).url || '');
  const { credentials: _c, ...rest } = init ?? {};
  return artificeFetch(url, rest);
}

const DEFAULT_CSRF = 'Artifice';

function headersToRecord(headers?: HeadersInit): Record<string, string> {
  const out: Record<string, string> = {};
  if (!headers) return out;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    headers.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      if (value) out[key] = value;
    }
    return out;
  }
  for (const [key, value] of Object.entries(headers as Record<string, string | undefined>)) {
    if (typeof value === 'string' && value) out[key] = value;
  }
  return out;
}

function deleteHeader(headers: Record<string, string>, name: string): void {
  const want = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === want) delete headers[key];
  }
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
  const want = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === want);
}

export function isValetAccountPath(input: string): boolean {
  let pathname = String(input || '').trim();
  try {
    if (/^https?:\/\//i.test(pathname)) pathname = new URL(pathname).pathname;
  } catch {
    /* keep raw */
  }
  const path = pathname.split('?')[0];
  if (path.startsWith('/api/terminal/auth/artifice')) return false;
  if (path.startsWith('/api/terminal/oauth')) return false;
  // LLM keys stay on Grail SQL. Valet (`auth.artifice.systems`) has no vault;
  // sending this prefix there 502s Agent Chat ("Proxy to … failed").
  if (path === '/api/terminal/account/llm-keys' || path.startsWith('/api/terminal/account/llm-keys/')) {
    return false;
  }
  return (
    path.startsWith('/api/terminal/auth') ||
    path.startsWith('/api/terminal/session') ||
    path.startsWith('/api/terminal/wallet') ||
    path.startsWith('/api/terminal/account') ||
    path.startsWith('/api/terminal/device-login') ||
    path.startsWith('/api/account')
  );
}

/** Absolute URL or same-origin path, optionally prefixed with the split-frontend API base. */
export function resolveArtificeApiUrl(input: string, apiBaseUrl?: string): string {
  const raw = String(input || '').trim();
  if (!raw) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  const valetBase = String(brand.authBaseUrl || '')
    .trim()
    .replace(/\/$/, '');
  if (valetBase && isValetAccountPath(path)) {
    return `${valetBase}${path}`;
  }
  const rawBase = apiBaseUrl ?? brand.apiBaseUrl;
  const base = String(rawBase || '')
    .trim()
    .replace(/\/$/, '');
  return base ? `${base}${path}` : path;
}

function assertTrustedArtificeOrigin(url: string, apiBaseUrl?: string): void {
  if (!/^https?:\/\//i.test(url)) return;

  const allowed = new Set<string>();
  const addOrigin = (value?: string) => {
    const raw = String(value || '').trim();
    if (!raw) return;
    try {
      allowed.add(new URL(raw).origin);
    } catch {
      /* invalid configured bases are rejected below */
    }
  };

  if (typeof window !== 'undefined' && window.location?.origin) {
    allowed.add(window.location.origin);
  }
  addOrigin(brand.apiBaseUrl);
  addOrigin(brand.authBaseUrl);
  addOrigin(apiBaseUrl);

  const targetOrigin = new URL(url).origin;
  if (!allowed.has(targetOrigin)) {
    throw new TypeError(`Artifice API origin is not trusted: ${targetOrigin}`);
  }
}

/**
 * Cookie session + Artifice access headers. Drops `Authorization` even if the
 * caller set it — that is the Easy Auth invariant.
 */
export function buildArtificeHeaders(
  headers?: HeadersInit,
  opts?: { auth?: ArtificeAuthMode; keepAuthorization?: boolean },
): Record<string, string> {
  const out = headersToRecord(headers);
  if (!opts?.keepAuthorization) deleteHeader(out, 'authorization');

  const creds = getFrontEndTenantCredentials();
  if (creds) {
    out['X-FrontEnd-Tenant-Id'] = creds.tenantId;
    out['X-FrontEnd-Tenant-Key'] = creds.tenantKey;
  }

  const next = opts?.auth === 'none' ? out : withSessionAccessHeaders(out);
  if (!hasHeader(next, 'x-requested-with')) next['X-Requested-With'] = DEFAULT_CSRF;
  return next;
}

function mergeAbortSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([a, b]);
  }
  const merged = new AbortController();
  const abort = () => merged.abort();
  if (a.aborted || b.aborted) {
    abort();
    return merged.signal;
  }
  a.addEventListener('abort', abort, { once: true });
  b.addEventListener('abort', abort, { once: true });
  return merged.signal;
}

export async function artificeFetch(input: string, init?: ArtificeFetchInit): Promise<Response> {
  const auth = init?.auth ?? 'session';
  const url = resolveArtificeApiUrl(input, init?.apiBaseUrl);
  assertTrustedArtificeOrigin(url, init?.apiBaseUrl);
  const valet = isValetAccountPath(input) || isValetAccountPath(url);
  const headers = new Headers(buildArtificeHeaders(init?.headers, { auth, keepAuthorization: valet }));
  const timeoutMs =
    typeof init?.timeoutMs === 'number' && Number.isFinite(init.timeoutMs) && init.timeoutMs > 0
      ? init.timeoutMs
      : 0;

  const { auth: _a, apiBaseUrl: _b, timeoutMs: _t, signal: externalSignal, ...rest } = init ?? {};
  let timer: ReturnType<typeof setTimeout> | undefined;
  let signal = externalSignal;
  if (timeoutMs > 0) {
    const timeoutController = new AbortController();
    timer = setTimeout(() => {
      const reason =
        typeof DOMException !== 'undefined'
          ? new DOMException(`Client request timed out after ${timeoutMs}ms (aborted)`, 'AbortError')
          : undefined;
      timeoutController.abort(reason);
    }, timeoutMs);
    signal = externalSignal
      ? mergeAbortSignals(externalSignal, timeoutController.signal)
      : timeoutController.signal;
  }

  try {
    return await fetch(url, {
      ...rest,
      credentials: 'include',
      headers,
      signal,
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export { TERMINAL_ACCESS_HEADER, TERMINAL_ACCESS_HEADER_JESTER } from '../utils/sessionAccessToken';
